import Image from 'next/image'
import {FC} from 'react'

import {BadgeColor} from '@/components/common/badges/Badge'
import {PricingOfferCard} from '@/components/common/cards/PricingOfferCard'
import {PricingOfferCardsSection} from '@/components/common/cards/PricingOfferCardsSection'
import {CheckmarkIcon} from '@/components/common/icons/CheckmarkIcon'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {usePreferences} from '@/components/context/Preferences'
import {getTrademarkRenewalJurisdictionContent} from '@/content/trademark-renewal/trademarkRenewal'
import {Trans, useTranslation} from '@/i18n'
import {Jurisdiction} from '@/utils/countries/constants'
import {useLanguage} from '@/utils/i18n/language'
import {getMinRegistrationPrice} from '@/utils/pricing'
import {calculateTmRenewalJurisdictionPrice} from '@/utils/pricing/renewal/calculations'
import {calculateTmOwnershipTransferCountryPrice} from '@/utils/pricing/tm-ownership-transfer/calculations'
import {Service} from '@/utils/service-request/types'
import {analytics} from '@/utils/tracking/analytics'
import {TrademarkOwnershipTransferJurisdiction} from '@/utils/trademark-ownership-transfer/constants'

type TmRenewalE2eProtectionCardsProps = {
  jurisdiction: Jurisdiction
}

export const TmRenewalE2eProtectionCards: FC<TmRenewalE2eProtectionCardsProps> = ({
  jurisdiction,
}) => {
  const {t} = useTranslation()
  const {currency} = usePreferences()
  const language = useLanguage()
  const {title, services, footer} = getTrademarkRenewalJurisdictionContent(
    jurisdiction,
    language,
  ).cards

  const getPrice = (key: Service) => {
    switch (key) {
      case Service.tmRenewal:
        return calculateTmRenewalJurisdictionPrice(jurisdiction, currency).total.vatBase
      case Service.tmRegistration:
        return getMinRegistrationPrice(currency, [], jurisdiction)
      case Service.tmAssignmentAgreement:
        return calculateTmOwnershipTransferCountryPrice(
          jurisdiction as TrademarkOwnershipTransferJurisdiction,
          currency,
        ).total.vatBase
      default:
        return 0
    }
  }

  return (
    <PricingOfferCardsSection
      title={t(title)}
      cards={Object.entries(services).map(
        (
          [
            key,
            {
              serviceTitle,
              title,
              imageSrc,
              imageAlt,
              badgeText,
              bulletPoints,
              buttonText,
              buttonLinkHref,
            },
          ],
          i,
        ) => {
          const price = getPrice(key as Service)

          return (
            <PricingOfferCard
              key={i}
              icon={<Image src={imageSrc} alt={imageAlt} width={40} height={40} />}
              title={t(title)}
              badge={{text: t(badgeText), color: BadgeColor.green, icon: <CheckmarkIcon />}}
              price={<LocalizedNumberFormat value={price} type="currency" />}
              bullets={(bulletPoints as {text: string; number?: number}[]).map((item, i) => (
                <Trans key={i} i18nKey={item.text} values={{number: item.number}} />
              ))}
              button={{
                text: t(buttonText),
                href: buttonLinkHref,
                onClick: () =>
                  analytics.track(`${serviceTitle} card clicked`, {
                    jurisdiction,
                    page: 'Trademark renewal',
                  }),
              }}
              isHighlighted={i === 0}
            />
          )
        },
      )}
      footer={{
        button: {
          text: t(footer.buttonText),
          linkHref: footer.buttonLinkHref,
          onClick: () => analytics.track('Show all services button clicked', {jurisdiction}),
        },
      }}
    />
  )
}
